import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, FileEdit, CalendarClock, MailOpen, MousePointerClick, Mail, Users, UserMinus, CheckCircle2, Eye } from 'lucide-react';
import { API, StatusBadge, fmtDate, cardCls, txtMain, txtSub } from './nlShared';
import { LoadingSpinner } from '../core';

const CARDS = [
  ['sent_campaigns', 'Campagnes envoyées', Send, '#22c55e'],
  ['drafts', 'Brouillons', FileEdit, '#eab308'],
  ['scheduled', 'Programmées', CalendarClock, '#3b82f6'],
  ['open_rate', 'Taux d\'ouverture', MailOpen, '#E9641A', '%'],
  ['ctr', 'CTR moyen', MousePointerClick, '#a855f7', '%'],
  ['emails_sent', 'Emails envoyés', Mail, '#229FA1'],
  ['subscribers', 'Abonnés', Users, '#3b82f6'],
  ['unsubscribes', 'Désinscriptions', UserMinus, '#ef4444'],
  ['delivered', 'Délivrés', CheckCircle2, '#22c55e'],
];

function MiniChart({ data, field, color, light, label }) {
  const max = Math.max(1, ...data.map(d => d[field]));
  return (
    <div className={`${cardCls(light)} p-4`} data-testid={`nl-chart-${field}`}>
      <p className={`text-xs font-semibold mb-3 ${txtSub(light)}`}>{label}</p>
      <div className="flex items-end gap-[3px] h-20">
        {data.map((d, i) => (
          <div key={i} className="flex-1 rounded-t transition-all" title={`${d.date} · ${d[field]}`}
            style={{ height: `${Math.max(4, (d[field] / max) * 100)}%`, background: color, opacity: 0.75 }} />
        ))}
      </div>
    </div>
  );
}

export function NlDashboard({ theme, onOpen }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/nl/overview`, { withCredentials: true }).then(r => setData(r.data)).catch(() => setData({ cards: {}, timeline: [], recent: [] }));
  }, []);

  if (!data) return <LoadingSpinner />;

  return (
    <div className="space-y-6" data-testid="nl-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {CARDS.map(([key, label, Icon, color, suffix]) => (
          <div key={key} className={`${cardCls(light)} p-4`} data-testid={`nl-card-${key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}1f` }}>
                <Icon size={14} style={{ color }} />
              </span>
            </div>
            <p className={`text-xl font-bold ${txtMain(light)}`}>{(data.cards[key] ?? 0).toLocaleString('fr-FR')}{suffix || ''}</p>
            <p className={`text-[11px] ${txtSub(light)}`}>{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniChart data={data.timeline} field="opens" color="#E9641A" light={light} label="Ouvertures (14 j)" />
        <MiniChart data={data.timeline} field="clicks" color="#a855f7" light={light} label="Clics (14 j)" />
        <MiniChart data={data.timeline} field="subs" color="#3b82f6" light={light} label="Nouveaux abonnés (14 j)" />
        <MiniChart data={data.timeline} field="unsubs" color="#ef4444" light={light} label="Désinscriptions (14 j)" />
      </div>

      <div className={`${cardCls(light)} p-5`} data-testid="nl-recent-campaigns">
        <p className={`text-sm font-bold mb-4 ${txtMain(light)}`}>Dernières campagnes</p>
        {data.recent.length === 0 ? (
          <p className={`text-sm ${txtSub(light)}`}>Aucune campagne pour le moment. Créez votre première newsletter !</p>
        ) : data.recent.map(c => (
          <div key={c.id} className={`flex items-center gap-4 py-3 border-b last:border-0 ${light ? 'border-gray-100' : 'border-white/5'}`}>
            <div className={`w-14 h-9 rounded overflow-hidden flex-none ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
              {c.thumb && <img src={c.thumb} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold truncate ${txtMain(light)}`}>{c.name}</p>
              <p className={`text-xs truncate ${txtSub(light)}`}>{c.subject || 'Sans objet'} · {fmtDate(c.updated_at)}</p>
            </div>
            <StatusBadge status={c.status} />
            <div className={`hidden sm:flex gap-4 text-xs ${txtSub(light)}`}>
              <span title="Taux d'ouverture">📬 {c.open_rate}%</span>
              <span title="Taux de clic">🖱 {c.ctr}%</span>
            </div>
            <button data-testid={`nl-recent-view-${c.id}`} onClick={() => onOpen(c.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#E9641A]/10 text-[#E9641A] hover:bg-[#E9641A] hover:text-white transition-colors">
              <Eye size={13} /> Voir
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
